import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Clock, Globe, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
    { icon: Mail, label: "EMAIL", value: "bettingnator.info@gmail.com" },
    { icon: Clock, label: "RESPONSE TIME", value: "Within 24 hours" },
    { icon: Globe, label: "COVERAGE", value: "European & international football" },
];

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        const onSubscribe = (e) => {
            setForm((f) => ({ ...f, subject: e.detail.subject, message: e.detail.message }));
        };
        window.addEventListener("bn:subscribe", onSubscribe);
        return () => window.removeEventListener("bn:subscribe", onSubscribe);
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent — we'll get back to you within 24 hours.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full bg-[#0B0F17] border border-bn-line rounded-lg px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-bn-blue focus:ring-2 focus:ring-bn-blue/20 transition-all";

    return (
        <section id="contact" data-testid="contact-us-section" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-14">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                >
                    <div className="font-mono text-[11px] tracking-[0.3em] text-bn-blue font-semibold mb-4">CONTACT US</div>
                    <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
                        Talk to the <span className="text-bn-blue text-glow-blue">team</span>
                    </h2>
                    <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-md">
                        Questions about subscriptions, the archive or how our doubles are produced — send us a message and a member of the team will reply.
                    </p>
                    <div className="mt-10 space-y-6">
                        {INFO.map((i) => (
                            <div key={i.label} className="flex items-center gap-4">
                                <span className="w-11 h-11 rounded-xl bg-bn-blue/10 border border-bn-blue/30 flex items-center justify-center shrink-0">
                                    <i.icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-bn-blue" />
                                </span>
                                <div>
                                    <div className="font-mono text-[10px] tracking-[0.2em] text-slate-500">{i.label}</div>
                                    <div className="text-sm font-semibold text-slate-200 mt-0.5">{i.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="bg-bn-card/60 backdrop-blur border border-bn-line rounded-2xl p-7 sm:p-9 space-y-5"
                >
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label htmlFor="contact-name" className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">NAME</label>
                            <input id="contact-name" data-testid="contact-form-name" required minLength={2} maxLength={100} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">EMAIL</label>
                            <input id="contact-email" data-testid="contact-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="contact-subject" className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">SUBJECT</label>
                        <input id="contact-subject" data-testid="contact-form-subject" required minLength={2} maxLength={150} value={form.subject} onChange={set("subject")} placeholder="What is this about?" className={inputCls} />
                    </div>
                    <div>
                        <label htmlFor="contact-message" className="block font-mono text-[10px] tracking-[0.2em] text-slate-500 mb-2">MESSAGE</label>
                        <textarea id="contact-message" data-testid="contact-form-message" required minLength={10} maxLength={3000} rows={5} value={form.message} onChange={set("message")} placeholder="Write your message..." className={`${inputCls} resize-none`} />
                    </div>
                    <button
                        data-testid="contact-form-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group w-full inline-flex items-center justify-center gap-2.5 py-4 rounded-full bg-bn-blue hover:bg-bn-bluehover disabled:opacity-60 text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,102,255,0.5)]"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />}
                        {sending ? "SENDING..." : "SEND MESSAGE"}
                    </button>
                </motion.form>
            </div>
        </section>
    );
};
